/**
 * Edit Plan Modules Modal
 * Checkbox group for managing licensed modules on a plan.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Layers } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import planService, { Plan } from '../../services/api/planService';
import { extractApiError } from '../../utils/apiError';
import { LICENSED_MODULES } from '@upskill/shared';
import type { LicensedModule } from '@upskill/shared';

interface EditPlanModulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
}

/** Human-readable labels for each licensed module key. Exported for reuse in EditPlanPermissionsModal. */
export const MODULE_LABELS: Record<LicensedModule, string> = {
  analytics: 'Analytics',
  clients: 'Clients',
  forms: 'Forms',
  links: 'Shareable Links',
  organization: 'Organization',
  reports: 'Reports',
  tasks: 'Tasks',
  team: 'Team',
  workflows: 'Workflows',
};

export const EditPlanModulesModal = ({ isOpen, onClose, plan }: EditPlanModulesModalProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Stable query key — plan.id is the only variable part
  const queryKey = useMemo(() => ['plan-modules', plan.id], [plan.id]);

  const { data: currentModules, isLoading: isLoadingModules } = useQuery({
    queryKey,
    queryFn: () => planService.getPlanModules(plan.id),
    enabled: isOpen,
  });

  // Sync fetched modules into local selection state; also clear success
  // feedback whenever the modal opens or the plan switches.
  useEffect(() => {
    if (currentModules) {
      setSelected(new Set(currentModules));
    }
  }, [currentModules]);

  useEffect(() => {
    setSuccessMessage(null);
  }, [isOpen, plan.id]);

  const updateMutation = useMutation({
    mutationFn: (modules: string[]) => planService.setPlanModules(plan.id, modules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSuccessMessage('Modules updated successfully.');
    },
  });

  const handleClose = () => {
    updateMutation.reset();
    setSuccessMessage(null);
    onClose();
  };

  const handleToggle = (module: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
    // Clear success message when user makes a new change
    setSuccessMessage(null);
  };

  const handleSave = () => {
    updateMutation.mutate(Array.from(selected));
  };

  const isSaveDisabled = updateMutation.isPending || isLoadingModules;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Modules — ${plan.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSave}
            disabled={isSaveDisabled}
            isLoading={updateMutation.isPending}
          >
            Save Modules
          </Button>
        </>
      }
    >
      <ModalBody>
        {updateMutation.isError && (
          <FeedbackMessage $variant="error">
            {extractApiError(updateMutation.error, 'Failed to update modules. Please try again.')}
          </FeedbackMessage>
        )}

        {successMessage && (
          <FeedbackMessage $variant="success">{successMessage}</FeedbackMessage>
        )}

        <FieldsetHeader>
          <Layers size={16} aria-hidden="true" />
          <FieldsetTitle>Licensed Modules</FieldsetTitle>
        </FieldsetHeader>

        <HelperText>
          Select the modules included in this plan. Changes take effect immediately for all
          organizations on this plan — entitlement caches are evicted on save.
        </HelperText>

        {isLoadingModules ? (
          <LoadingText>Loading current modules…</LoadingText>
        ) : (
          <CheckboxGrid role="group" aria-label="Licensed modules">
            {LICENSED_MODULES.map((mod) => {
              const id = `module-${plan.id}-${mod}`;
              const isChecked = selected.has(mod);
              return (
                <CheckboxRow key={mod}>
                  <HiddenCheckbox
                    id={id}
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggle(mod)}
                    disabled={updateMutation.isPending}
                  />
                  <CheckboxLabel htmlFor={id} $checked={isChecked}>
                    <CheckboxBox $checked={isChecked} aria-hidden="true">
                      {isChecked && <Checkmark />}
                    </CheckboxBox>
                    {MODULE_LABELS[mod]}
                  </CheckboxLabel>
                </CheckboxRow>
              );
            })}
          </CheckboxGrid>
        )}
      </ModalBody>
    </Modal>
  );
};

export default EditPlanModulesModal;

// ==================== Styled Components ====================

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FeedbackMessage = styled.div<{ $variant: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  ${({ $variant, theme }) =>
    $variant === 'error'
      ? `
    background: ${theme.colors.status.error.bg};
    border: 1px solid ${theme.colors.status.error.border};
    color: ${theme.colors.status.error.text};
  `
      : `
    background: ${theme.colors.status.success.bg};
    border: 1px solid ${theme.colors.status.success.border};
    color: ${theme.colors.status.success.text};
  `}
`;

const FieldsetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const FieldsetTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[2]};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
`;

/** Visually hidden but accessible native checkbox */
const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const CheckboxLabel = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ $checked, theme }) =>
    $checked ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: ${({ $checked, theme }) =>
    $checked
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  cursor: pointer;
  user-select: none;
  line-height: 1.4;

  &:hover > span {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const CheckboxBox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.primary.base : theme.colors.borders.medium};
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.primary.base : theme.colors.backgrounds.white};
  transition: ${({ theme }) => theme.transitions.fast};
`;

/** Simple SVG checkmark */
const Checkmark = () => (
  <svg
    width="10"
    height="8"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 3.5L3.5 6.5L9 1"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
